import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useUpdateEntity } from "@/hooks/use-data";
import { ApiError } from "@/lib/api";
import type { ConflictBody, EntityRecord, TargetEnv } from "@/lib/types";
import { useState } from "react";
import {
  AccountForm,
  type AccountFormState,
  diffAccount,
  initialAccount,
} from "./appdata-account-form";
import {
  PersonalDetailsForm,
  type PersonalDetailsFormState,
  diffPersonalDetails,
  initialPersonalDetails,
} from "./appdata-personal-details-form";
import {
  PhoneAppdataForm,
  type PhoneAppdataFormState,
  diffPhoneAppdata,
  initialPhoneAppdata,
} from "./appdata-phone-form";
import { TargetEnvRadio } from "./target-env-radio";

// IR-661 — modal Dialog for editing an entity. Switches the appdata
// sub-form on `entity.type`: phone → PhoneAppdataForm, im_account →
// PersonalDetailsForm, social_media_account/account → AccountForm.
// Caller must pass `type`/`app_type`/`appdata` (loaded via useEntity(id)
// which now reads these from /api/ops/v1/data/entities/:id GET).

export type EntityEditType = "phone" | "im_account" | "social_media_account" | "account";

export interface EntityEditExtras {
  type: EntityEditType;
  /** Read-only display value on the account sub-form. Pulled from
   *  Entity.app_type which is in `_FORBIDDEN_EDIT_FIELDS` on the
   *  backend. */
  app_type?: string | null;
  /** Pre-loaded appdata seed for the sub-form. The contract here is
   *  per-type and matches what `validate_appdata_for_type` accepts on
   *  the backend (api/ops/serializers_data.py:293). */
  appdata?: Record<string, unknown>;
}

export type EntityEditEntity = EntityRecord & EntityEditExtras;

interface Props {
  entity: EntityEditEntity;
  /** Echoed back as `X-If-Unmodified-Since` for optimistic-concurrency
   *  protection. When the backend's row is newer the PUT returns 409 and
   *  we swap the body for a Reload prompt. */
  system_update_time?: string;
  onClose: () => void;
}

interface EntityCoreFormState {
  name: string;
  classification: string;
  risk_score: string;
}

function initialCore(entity: EntityRecord): EntityCoreFormState {
  return {
    name: entity.name ?? "",
    classification: entity.classifications[0] ?? "",
    risk_score: entity.risk_score == null ? "" : String(entity.risk_score),
  };
}

function dirtyCore(form: EntityCoreFormState, entity: EntityRecord): Partial<EntityRecord> {
  const out: Partial<EntityRecord> = {};
  if (form.name !== (entity.name ?? "")) out.name = form.name;
  if (form.classification !== (entity.classifications[0] ?? "")) {
    // The backend's EntityUpdateSerializer accepts a single
    // classification string and stores it as the canonical value; the
    // FE EntityRecord exposes an array. We send the new single value
    // either as a 1-element array (round-trip) or — when blanked —
    // as an empty array.
    out.classifications = form.classification ? [form.classification] : [];
  }
  if (form.risk_score !== (entity.risk_score == null ? "" : String(entity.risk_score))) {
    out.risk_score = form.risk_score === "" ? null : Number(form.risk_score);
  }
  return out;
}

type AnyAppdataState = PhoneAppdataFormState | PersonalDetailsFormState | AccountFormState | null;

function initialAppdata(type: EntityEditType, seed: Record<string, unknown>): AnyAppdataState {
  switch (type) {
    case "phone":
      return initialPhoneAppdata(seed);
    case "im_account":
      return initialPersonalDetails(seed);
    case "social_media_account":
    case "account":
      return initialAccount(seed);
    default:
      return null;
  }
}

function diffAppdata(
  type: EntityEditType,
  form: AnyAppdataState,
  seed: Record<string, unknown>,
): Record<string, unknown> {
  if (form == null) return {};
  switch (type) {
    case "phone":
      return diffPhoneAppdata(form as PhoneAppdataFormState, seed);
    case "im_account":
      return diffPersonalDetails(form as PersonalDetailsFormState, seed);
    case "social_media_account":
    case "account":
      return diffAccount(form as AccountFormState, seed);
    default:
      return {};
  }
}

function isCoreDirty(form: EntityCoreFormState, entity: EntityRecord): boolean {
  if (form.name !== (entity.name ?? "")) return true;
  if (form.classification !== (entity.classifications[0] ?? "")) return true;
  if (form.risk_score !== (entity.risk_score == null ? "" : String(entity.risk_score))) {
    return true;
  }
  return false;
}

export function EntityEditDialog({ entity, system_update_time, onClose }: Props) {
  const seed = entity.appdata ?? {};
  const [coreForm, setCoreForm] = useState<EntityCoreFormState>(() => initialCore(entity));
  const [appdataForm, setAppdataForm] = useState<AnyAppdataState>(() =>
    initialAppdata(entity.type, seed),
  );
  const [conflict, setConflict] = useState<ConflictBody | null>(null);
  // IR-661 Rotem reopen (2026-05-11): required env scope, no default.
  const [targetEnv, setTargetEnv] = useState<TargetEnv | null>(null);
  const mutation = useUpdateEntity();

  const appdataDelta = diffAppdata(entity.type, appdataForm, seed);
  const dirty = isCoreDirty(coreForm, entity) || Object.keys(appdataDelta).length > 0;
  const errorMessage =
    mutation.error && !(mutation.error instanceof ApiError && mutation.error.status === 409)
      ? (mutation.error.message ?? null)
      : null;

  function onSave() {
    if (targetEnv === null) return;
    const body: Record<string, unknown> = { ...dirtyCore(coreForm, entity) };
    if (Object.keys(appdataDelta).length > 0) {
      body.appdata = appdataDelta;
    }
    mutation.mutate(
      {
        id: entity.id,
        data: body as Partial<EntityRecord>,
        ifUnmodifiedSince: system_update_time,
        targetEnv,
      },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setConflict(err.body as ConflictBody);
          }
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, padding: 20 }}>
        {conflict ? (
          <ConflictReloadBody conflict={conflict} onReload={onClose} />
        ) : (
          <>
            <div>
              <DialogTitle>Edit entity</DialogTitle>
              <DialogDescription>
                {targetEnv === null
                  ? "Pick a target environment to enable Save."
                  : targetEnv === "both"
                    ? "Edits fan out to BOTH prod and dev. Per-env results return in the response envelope."
                    : `Edits apply to ${targetEnv === "prod" ? "production" : "development"} only.`}
              </DialogDescription>
            </div>

            <TargetEnvRadio value={targetEnv} onChange={setTargetEnv} actionVerb="edit" />

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label
                htmlFor="ir661-edit-entity-name"
                style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
              >
                Name
                <input
                  id="ir661-edit-entity-name"
                  className="iris-input"
                  value={coreForm.name}
                  onChange={(e) => setCoreForm({ ...coreForm, name: e.target.value })}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </label>

              <label
                htmlFor="ir661-edit-entity-classification"
                style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
              >
                Classification
                <input
                  id="ir661-edit-entity-classification"
                  className="iris-input"
                  value={coreForm.classification}
                  onChange={(e) => setCoreForm({ ...coreForm, classification: e.target.value })}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </label>

              <label
                htmlFor="ir661-edit-entity-risk"
                style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
              >
                Risk Score
                <input
                  id="ir661-edit-entity-risk"
                  type="number"
                  min={0}
                  max={10}
                  step="0.01"
                  className="iris-input"
                  value={coreForm.risk_score}
                  onChange={(e) => setCoreForm({ ...coreForm, risk_score: e.target.value })}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </label>
            </div>

            {/* ─── per-type appdata sub-form ─── */}
            <AppdataSubForm
              type={entity.type}
              appType={entity.app_type}
              form={appdataForm}
              setForm={setAppdataForm}
            />

            {errorMessage ? (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--iris-neon-pink)",
                  padding: "8px 10px",
                  border: "1px solid var(--iris-neon-pink)",
                  borderRadius: 6,
                }}
              >
                {errorMessage}
              </div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: "auto",
              }}
            >
              <button
                type="button"
                className="iris-btn"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Discard
              </button>
              <button
                type="button"
                className="iris-btn iris-btn--primary"
                onClick={onSave}
                disabled={!dirty || mutation.isPending || targetEnv === null}
              >
                {mutation.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface SubProps {
  type: EntityEditType;
  appType: string | null | undefined;
  form: AnyAppdataState;
  setForm: (next: AnyAppdataState) => void;
}

function AppdataSubForm({ type, appType, form, setForm }: SubProps) {
  switch (type) {
    case "phone":
      return (
        <PhoneAppdataForm form={form as PhoneAppdataFormState} setForm={(next) => setForm(next)} />
      );
    case "im_account":
      return (
        <PersonalDetailsForm
          form={form as PersonalDetailsFormState}
          setForm={(next) => setForm(next)}
        />
      );
    case "social_media_account":
    case "account":
      return (
        <AccountForm
          form={form as AccountFormState}
          setForm={(next) => setForm(next)}
          appType={appType}
        />
      );
    default:
      return null;
  }
}

function ConflictReloadBody({
  conflict,
  onReload,
}: {
  conflict: ConflictBody;
  onReload: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <DialogTitle>Stale data</DialogTitle>
      <DialogDescription>
        {conflict.detail ||
          "The row was modified by someone else while you were editing. Reload and retry."}
      </DialogDescription>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="iris-btn iris-btn--primary" onClick={onReload}>
          Reload
        </button>
      </div>
    </div>
  );
}
